import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { invokeAdminThemeSettings } from '@/lib/admin-theme-settings';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Check, RotateCcw } from 'lucide-react';
import { AdminPage, PageHeader, Panel, StatusChip, adminButton, useAdminPageTitle } from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import { THEME_PRESETS } from '@/lib/theme-presets';
import { ThemeLivePreview, type ThemePreviewOverrides } from '@/components/admin/theme/ThemeLivePreview';

// Shared single source of truth — same presets the runtime theme engine applies.
const themes = THEME_PRESETS;

const buttonStyles = [
  { id: 'rounded', name: 'Rounded', preview: 'rounded-xl' },
  { id: 'pill', name: 'Pill', preview: 'rounded-full' },
  { id: 'sharp', name: 'Sharp', preview: 'rounded-none' },
  { id: 'soft', name: 'Soft', preview: 'rounded-lg' },
];

const headingFonts = [
  { id: 'DM Sans', name: 'DM Sans (Sans)' },
  { id: 'Palo', name: 'Palo (Display)' },
  { id: 'Playfair Display', name: 'Playfair Display (Serif)' },
  { id: 'Montserrat', name: 'Montserrat (Sans)' },
  { id: 'Raleway', name: 'Raleway (Elegant)' },
];

const bodyFonts = [
  { id: 'DM Sans', name: 'DM Sans' },
  { id: 'Inter', name: 'Inter' },
  { id: 'Open Sans', name: 'Open Sans' },
  { id: 'Lato', name: 'Lato' },
];

const ThemeAdmin = () => {
  useAdminPageTitle('Theme');
  const { orgId, canWrite } = useAdminOrg();
  const { isOrgOwner, isPlatformAdmin } = useAuth();
  const canEditTheme = canWrite && (isOrgOwner || isPlatformAdmin);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [buttonStyle, setButtonStyle] = useState('rounded');
  const [headingFont, setHeadingFont] = useState('Palo');
  const [bodyFont, setBodyFont] = useState('DM Sans');
  const [layoutStyle, setLayoutStyle] = useState('classic');
  const [primaryColor, setPrimaryColor] = useState<string | null>(null);
  const [buttonTextColor, setButtonTextColor] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchThemeSettings();
  // Fetch after mount; fetchThemeSettings guards missing orgId internally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchThemeSettings = async () => {
    try {
      if (!orgId) return;
      const { themeSettings: data } = await invokeAdminThemeSettings({ action: 'fetch', orgId });

      if (data) {
        setSelectedTheme(data.theme_name || 'classic');
        setButtonStyle(data.button_style || 'rounded');
        setHeadingFont(data.heading_font || 'Palo');
        setBodyFont(data.body_font || 'DM Sans');
        setLayoutStyle(data.layout_style || 'classic');
        setPrimaryColor(data.primary_color || null);
        setButtonTextColor(data.button_text_color || null);
      }
    } catch (error: unknown) {
      console.error('Error fetching theme settings:', error);
      toast.error('Failed to load theme settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEditTheme) {
      toast.error(canWrite ? 'Only the business owner can save theme changes' : 'Read-only while impersonating.');
      return;
    }
    setSaving(true);
    try {
      const themeData = {
        organization_id: orgId,
        theme_name: selectedTheme,
        button_style: buttonStyle,
        heading_font: headingFont,
        body_font: bodyFont,
        layout_style: layoutStyle,
        primary_color: primaryColor,
        button_text_color: buttonTextColor,
      };

      const { themeSettings: savedSettings } = await invokeAdminThemeSettings({ action: 'save', orgId, themeSettings: themeData });
      window.dispatchEvent(new CustomEvent('theme-settings-updated', { detail: savedSettings ?? themeData }));

      toast.success('Theme settings saved');
      setHasChanges(false);
    } catch (error: unknown) {
      console.error('Error saving theme settings:', error);
      toast.error('Failed to save theme settings');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = (themeId: string) => {
    const theme = themes.find(t => t.id === themeId);
    if (theme) {
      setSelectedTheme(themeId);
      setHeadingFont(theme.headingFont);
      setBodyFont(theme.bodyFont);
      setLayoutStyle(theme.layoutStyle);
      setPrimaryColor(null);
      setButtonTextColor(null);
      setButtonStyle(theme.buttonStyle);
    }
    setHasChanges(true);
  };

  const currentTheme = themes.find(t => t.id === selectedTheme) || themes[0];
  // The selected theme previews with the admin's current overrides; others show pure presets.
  const selectedOverrides = useMemo<ThemePreviewOverrides>(
    () => ({ headingFont, bodyFont, buttonStyle, primaryColor, buttonTextColor }),
    [headingFont, bodyFont, buttonStyle, primaryColor, buttonTextColor],
  );

  const activeButtonRadius =
    buttonStyle === 'pill' ? '9999px' : buttonStyle === 'sharp' ? '0' : buttonStyle === 'soft' ? '0.5rem' : '0.75rem';
  const effectivePrimary = primaryColor || currentTheme.primaryColor;
  const effectiveButtonText = buttonTextColor || '#ffffff';

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AdminPage className="adm-form">
      <PageHeader
        description="Choose a complete theme with distinct personality"
        actions={
          !canEditTheme ? (
            <StatusChip tone="grey" dot={false}>
              View only
            </StatusChip>
          ) : hasChanges ? (
            <StatusChip tone="amber">Unsaved changes</StatusChip>
          ) : null
        }
      />

      <div className="grid gap-4 md:gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4 md:space-y-5">
          {/* Theme Selection */}
          <Panel
            title="Choose Your Theme"
            description="Each theme includes unique fonts, layouts, and visual styles"
            actions={<span className="adm-chip adm-chip--grey adm-num">{themes.length} themes</span>}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
              {themes.map((theme) => {
                const selected = selectedTheme === theme.id;
                return (
                  <div
                    key={theme.id}
                    data-testid={`theme-card-${theme.id}`}
                    className={cn(
                      'group relative rounded-xl border bg-card p-2.5 text-left transition-all',
                      selected
                        ? 'border-primary/60 shadow-[0_0_0_3px_hsl(var(--primary)/0.14)]'
                        : 'border-border hover:border-primary/35 hover:shadow-[var(--adm-shadow-sm)]',
                    )}
                  >
                    {/* The preview is the real site in an iframe; clicking it selects the theme like the title does. */}
                    <div
                      className={cn('mb-2.5', canEditTheme && 'cursor-pointer')}
                      onClick={() => canEditTheme && handleThemeChange(theme.id)}
                    >
                      <ThemeLivePreview presetId={theme.id} presetName={theme.name} overrides={selected ? selectedOverrides : undefined} />
                    </div>

                    <button
                      type="button"
                      onClick={() => canEditTheme && handleThemeChange(theme.id)}
                      disabled={!canEditTheme}
                      aria-pressed={selected}
                      className="block w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
                    >
                    <div className="space-y-1 px-1 pb-0.5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-[13.5px] font-semibold text-foreground">{theme.name}</h3>
                        <span
                          className={cn(
                            'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors',
                            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-transparent',
                          )}
                          aria-hidden="true"
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                      </div>
                      <p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">{theme.description}</p>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <span className="adm-chip adm-chip--grey">{theme.headingFont}</span>
                        <span className="adm-chip adm-chip--grey">{theme.bodyFont}</span>
                      </div>
                    </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Typography Override */}
          <Panel title="Typography" description="Override the theme's default fonts if needed">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
              <div className="space-y-2">
                <Label htmlFor="theme-heading-font">Heading Font</Label>
                <Select
                  value={headingFont}
                  onValueChange={(v) => { if (canEditTheme) { setHeadingFont(v); setHasChanges(true); } }}
                  disabled={!canEditTheme}
                >
                  <SelectTrigger id="theme-heading-font">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {headingFonts.map((font) => (
                      <SelectItem key={font.id} value={font.id}>
                        {font.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="adm-hint">Theme default: {currentTheme.headingFont}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="theme-body-font">Body Font</Label>
                <Select
                  value={bodyFont}
                  onValueChange={(v) => { if (canEditTheme) { setBodyFont(v); setHasChanges(true); } }}
                  disabled={!canEditTheme}
                >
                  <SelectTrigger id="theme-body-font">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {bodyFonts.map((font) => (
                      <SelectItem key={font.id} value={font.id}>
                        {font.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="adm-hint">Theme default: {currentTheme.bodyFont}</p>
              </div>
            </div>
          </Panel>

          <div className="grid gap-4 md:gap-5 lg:grid-cols-2">
            {/* Button Style */}
            <Panel title="Button Style" description="Override the theme's button corners">
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-2 2xl:grid-cols-4">
                {buttonStyles.map((style) => {
                  const selected = buttonStyle === style.id;
                  return (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => { if (canEditTheme) { setButtonStyle(style.id); setHasChanges(true); } }}
                      disabled={!canEditTheme}
                      aria-pressed={selected}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-xl border px-2 py-3 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed',
                        selected
                          ? 'border-primary/50 bg-secondary/60 shadow-[0_0_0_3px_hsl(var(--primary)/0.12)]'
                          : 'border-border bg-card hover:border-primary/35',
                      )}
                    >
                      <div
                        className={cn('px-3.5 py-1.5 text-[12px] font-medium', style.preview)}
                        style={{ backgroundColor: effectivePrimary, color: effectiveButtonText }}
                      >
                        Button
                      </div>
                      <span className={cn('text-[12.5px]', selected ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground')}>
                        {style.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Panel>

            {/* Custom Colors */}
            <Panel title="Custom Colors" description="Override theme colors with your brand (optional)">
              <div className="adm-divide -my-2">
                <ColorField
                  id="theme-primary-color"
                  label="Primary / Button Color"
                  value={effectivePrimary}
                  overridden={Boolean(primaryColor)}
                  disabled={!canEditTheme}
                  onChange={(value) => { if (canEditTheme) { setPrimaryColor(value); setHasChanges(true); } }}
                  onReset={() => { if (canEditTheme) { setPrimaryColor(null); setHasChanges(true); } }}
                />
                <ColorField
                  id="theme-button-text-color"
                  label="Button Text Color"
                  value={effectiveButtonText}
                  overridden={Boolean(buttonTextColor)}
                  disabled={!canEditTheme}
                  onChange={(value) => { if (canEditTheme) { setButtonTextColor(value); setHasChanges(true); } }}
                  onReset={() => { if (canEditTheme) { setButtonTextColor(null); setHasChanges(true); } }}
                />
              </div>
            </Panel>
          </div>
        </div>

        {/* Live preview */}
        <aside className="min-w-0 xl:sticky xl:top-[88px] xl:self-start">
          <Panel title="Preview" description="Your homepage in this theme">
            <div className="space-y-4">
              <ThemeLivePreview presetId={currentTheme.id} presetName={currentTheme.name} overrides={selectedOverrides} />
              <dl className="adm-divide rounded-xl border border-border text-[12.5px]">
                {[
                  ['Theme', currentTheme.name],
                  ['Headings', headingFont],
                  ['Body', bodyFont],
                  ['Buttons', buttonStyles.find((s) => s.id === buttonStyle)?.name ?? buttonStyle],
                ].map(([term, value]) => (
                  <div key={term} className="flex items-center justify-between gap-3 px-3 py-2">
                    <dt className="text-muted-foreground">{term}</dt>
                    <dd className="truncate font-medium text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
              <div>
                <p className="adm-label mb-2">Button Preview</p>
                <div className="flex items-center justify-center rounded-xl border border-dashed border-border bg-muted/40 py-6">
                  <div
                    className="inline-block px-6 py-3 text-sm font-medium shadow-sm"
                    style={{ backgroundColor: effectivePrimary, color: effectiveButtonText, borderRadius: activeButtonRadius }}
                  >
                    Sample Button
                  </div>
                </div>
              </div>
            </div>
          </Panel>
        </aside>
      </div>

      {/* Save bar */}
      {hasChanges && canEditTheme && (
        <div className="pointer-events-none sticky bottom-4 z-30 flex justify-end">
          <div className="adm-panel pointer-events-auto flex w-full items-center gap-3 p-2 shadow-[var(--adm-shadow-md)] sm:w-auto sm:pl-4">
            <p className="hidden min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground sm:block">
              You have unsaved theme changes.
            </p>
            <Button onClick={handleSave} disabled={saving} size="sm" className={cn(adminButton.primary, 'w-full shrink-0 sm:w-auto')}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      )}
    </AdminPage>
  );
};

interface ColorFieldProps {
  id: string;
  label: string;
  value: string;
  overridden: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
}

/** Colour swatch + hex readout + reset, one row. */
const ColorField = ({ id, label, value, overridden, disabled, onChange, onReset }: ColorFieldProps) => (
  <div className="flex flex-wrap items-center gap-3 py-3">
    <input
      id={id}
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-9 w-11 shrink-0 cursor-pointer rounded-lg border border-border bg-card p-1 shadow-[var(--adm-shadow-xs)] disabled:cursor-not-allowed disabled:opacity-60"
    />
    <div className="min-w-0 flex-1">
      <Label htmlFor={id} className="block">{label}</Label>
      <p className="mt-0.5 font-mono text-[12px] uppercase text-muted-foreground">
        {value}
        {!overridden && <span className="ml-1.5 font-sans normal-case">· theme default</span>}
      </p>
    </div>
    {overridden && (
      <Button variant="ghost" size="sm" onClick={onReset} disabled={disabled} className={cn(adminButton.ghost, 'h-8')}>
        <RotateCcw className="h-3.5 w-3.5" />
        Reset
      </Button>
    )}
  </div>
);

export default ThemeAdmin;
